import { useEffect, useState, type ReactNode } from 'react';
import { verifyPassword } from '../lib/reauth';
import { Button } from './Button';
import { TextField } from './inputs/TextField';
import { Modal } from './Modal';

/**
 * Confirmation for an action triggered OUTSIDE a modal — reference §5.
 *
 * Not for confirming a form: a form's own confirm checkbox gates its primary button and
 * fires directly. Stacking a dialog on top of an open modal is the "one modal, never
 * stack" violation.
 *
 * Two escalating safeguards, both off by default:
 *
 * - `requireConfirm` adds the shell's standard confirm tick.
 * - `requireCredential` adds re-authentication: the caller types their **own** password and
 *   it is verified before the action runs at all.
 *
 * A destructive action — delete, deactivate, clear — takes **both**.
 */
/**
 * THE WORD ON EVERY CONFIRMATION IN THE PRODUCT — product call, 01 Oct 2026, in the reader's own
 * words: *only use "Submit" to standardize modal triggers. "Confirm" if confirm modal*.
 *
 * It was the ACT: Delete, Restore, Retire, Withdraw, Deactivate, Clear data, Approve, Register,
 * Complete — about forty spellings of one control, a third of them a ternary picking between two
 * of them. What the reader is being asked is the same question every time, and the TITLE and the
 * BODY are what say which act it is about; a verb on the button repeats one of them and leaves
 * the next dialog free to pick a different word for the same thing.
 *
 * **It lives here rather than at the call site** for the reason `CONFIRM_SENTENCE` does: a
 * default a caller may override is one that drifts, and `test:conventions` fails the build on a
 * literal passed for either of these.
 */
const CONFIRM_LABEL = 'Confirm';

const CONFIRMING_LABEL = 'Confirming';

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = CONFIRM_LABEL,
  confirmingLabel = CONFIRMING_LABEL,
  danger = false,
  loading = false,
  id,
  disabled = false,
  requireConfirm = false,
  requireCredential = false,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Runs the action. With `requireCredential`, it receives the one-time proof the password
   * confirmation issued — an endpoint that must be password-confirmed whoever calls it spends
   * that proof (`reauthHeaders`). A caller whose endpoint does not ask for one ignores it.
   */
  onConfirm: (reauthentication?: string) => void | Promise<void>;
  title: string;
  body: ReactNode;
  /**
   * OVERRIDE IT ONLY WHERE THE BUTTON IS NOT A CONFIRMATION.
   *
   * The word is `Confirm` and the component owns it — see `CONFIRM_LABEL`. A dialog that turns
   * into a REPORT is the one shape that legitimately needs another: `<BulkDeactivateDialog>`
   * ends on a list of what failed, and its button then dismisses rather than confirming.
   */
  confirmLabel?: string;
  /** Present-progressive, shown while in flight. Overridden with `confirmLabel` or not at all. */
  confirmingLabel?: string;
  danger?: boolean;
  loading?: boolean;
  id: string;
  disabled?: boolean;
  /**
   * Gates the confirm button behind the shell's standard confirm checkbox.
   *
   * For an action whose consequence is wide or hard to picture — retiring every record in a
   * module, say. A plain confirm dialog is two clicks in the same place, which a reader
   * dismisses on reflex; the tick makes them touch a different control and read the sentence
   * that sits beside it.
   */
  requireConfirm?: boolean;
  /**
   * Requires the caller to re-enter their own password, verified server-side, before the
   * action runs.
   *
   * A tick proves someone clicked; a password proves it is still the person who signed in.
   * That is the difference that matters for anything destructive, because the realistic
   * threat is an unattended session rather than a careless owner.
   *
   * The password is sent, checked, and dropped. It is never stored, never sent with the
   * action itself, and cleared from state the moment the dialog closes.
   */
  requireCredential?: boolean;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState('');
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  /*
    Every visit starts clean — no inherited tick, and no password left in state from the last
    time this dialog was opened. Clearing on OPEN rather than on close also covers the dialog
    being dismissed by a route change or an unmount.
  */
  useEffect(() => {
    if (!open) return;

    setConfirmed(false);
    setPassword('');
    setCredentialError(null);
    setVerifying(false);
  }, [open]);

  const busy = loading || verifying;

  async function handleConfirm() {
    if (!requireCredential) {
      await onConfirm();
      return;
    }

    setVerifying(true);
    setCredentialError(null);

    let proof: string;

    try {
      // Verified BEFORE the action, never alongside it: a destructive call must not be sent
      // at all until the caller has proved who they are.
      proof = (await verifyPassword(password)).token;
    } catch {
      /*
        Deliberately not the server's message. The endpoint returns the same generic failure
        the login form does, and repeating anything more specific here would start narrating
        the account's state to whoever is sitting at the machine.
      */
      setCredentialError('That password is not correct.');
      setVerifying(false);
      return;
    }

    // Dropped as soon as it has served its purpose — what travels to the action is the proof,
    // never the password.
    setPassword('');
    setVerifying(false);

    await onConfirm(proof);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      // `sm` is reserved for pure confirmations — this is the one place it belongs.
      size="sm"
      id={id}
      // The shell owns the markup and the wording — CLAUDE.md, "never markup you write".
      confirm={
        requireConfirm
          ? { id: `${id}-ack`, checked: confirmed, onChange: setConfirmed, disabled: busy }
          : undefined
      }
      footer={
        <>
          <Button id={`${id}-cancel`} variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            id={`${id}-confirm`}
            variant={danger ? 'danger' : 'primary'}
            onClick={() => void handleConfirm()}
            loading={busy}
            loadingLabel={verifying ? 'Verifying' : confirmingLabel}
            disabled={
              disabled ||
              (requireConfirm && !confirmed) ||
              (requireCredential && password.length === 0)
            }
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="text-sm text-slate-600">{body}</div>

        {/*
          The credential sits below the message and above the tick, which is the order the
          reader works through: what is about to happen, then prove it is you, then confirm.
        */}
        {requireCredential ? (
          <TextField
            id={`${id}-password`}
            name="current-password"
            type="password"
            label="Your password"
            description="Confirms it is still you at this machine before anything is changed. It is checked and discarded, never stored."
            autoComplete="current-password"
            value={password}
            error={credentialError ?? undefined}
            disabled={busy}
            onChange={(event) => {
              setPassword(event.target.value);
              if (credentialError) setCredentialError(null);
            }}
          />
        ) : null}
      </div>
    </Modal>
  );
}
